'use client';

import { useState } from 'react';
import Image from 'next/image';

export interface VideoFacadeProps {
  /** Embed URL (player.vimeo.com/... or youtube embed). */
  videoUrl: string;
  /** Poster image path (self-hosted, goes through the optimizer). */
  poster: string;
  title: string;
  /** Passed to the poster Image; describe the rendered slot width. */
  sizes?: string;
  /** Off when the facade sits flush inside a card that clips its own corners. */
  rounded?: boolean;
  /** Controlled mode: the parent owns play state (e.g. a Watch button beside the poster). */
  playing?: boolean;
  onPlay?: () => void;
}

/**
 * Poster-and-play-button stand-in for an embedded video player. The real
 * iframe (and Vimeo's ~323KB player bundle + stream segments) loads only
 * when the visitor actually presses play — on pages where the video is
 * decoration, most never do. Autoplays on click so the visitor doesn't
 * press play twice.
 */
export function VideoFacade({
  videoUrl,
  poster,
  title,
  sizes,
  rounded = true,
  playing: playingProp,
  onPlay,
}: VideoFacadeProps) {
  const radius = rounded ? 'rounded-2xl' : '';
  const [playingState, setPlayingState] = useState(false);
  const playing = playingProp ?? playingState;
  const play = () => {
    setPlayingState(true);
    onPlay?.();
  };

  if (playing) {
    return (
      <iframe
        src={`${videoUrl}${videoUrl.includes('?') ? '&' : '?'}autoplay=1`}
        title={title}
        allow="autoplay; fullscreen; picture-in-picture"
        className={`aspect-video w-full ${radius}`}
      />
    );
  }

  return (
    <button
      type="button"
      aria-label={`Play video: ${title}`}
      onClick={play}
      className={`group relative block aspect-video w-full cursor-pointer overflow-hidden ${radius}`}
    >
      <Image
        src={poster}
        width={1280}
        height={720}
        alt=""
        priority
        fetchPriority="high"
        quality={80}
        {...(sizes ? { sizes } : {})}
        className="absolute inset-0 size-full object-cover transition-transform duration-300 group-hover:scale-105"
      />
      <span className="absolute inset-0 bg-brand-navy/15 transition-colors group-hover:bg-brand-navy/5" />
      <span className="absolute top-1/2 left-1/2 flex h-14 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-xl bg-black/60 shadow-lg backdrop-blur-sm transition-all group-hover:scale-105 group-hover:bg-black/75">
        <svg aria-hidden viewBox="0 0 24 24" className="ml-0.5 size-8 fill-white">
          <path d="M8 5.14v13.72L19 12 8 5.14Z" />
        </svg>
      </span>
    </button>
  );
}
